import { ConfigService } from './config.service.js';
import { platform, release, arch, hostname, homedir } from 'os';
import { version as nodeVersion } from 'process';
import packageJson from '../../package.json' with { type: 'json' };

/**
 * Tracking action categories
 */
export type TrackingAction =
    | 'init'
    | 'download'
    | 'version_check'
    | 'sync'
    | 'update'
    | 'redmine_check'
    | 'redmine_sync_issue'
    | 'redmine_sync_project'
    | 'vscode'
    | 'info'
    | 'check';

export interface TrackingDetail {
    [key: string]: unknown;
}

/**
 * Get basic system info for logging
 */
function getSystemInfo(): Record<string, string> {
    return {
        os: platform(),
        osRelease: release(),
        arch: arch(),
        nodeVersion: nodeVersion,
        hostname: hostname(),
        homeDir: homedir(),
    };
}

/**
 * Service for tracking CLI usage to server
 * Tracking is fire-and-forget, errors are silently ignored
 */
export class TrackingService {
    private configService: ConfigService;

    constructor() {
        this.configService = new ConfigService();
    }

    /**
     * Track a CLI action (fire-and-forget)
     * WARNING: Process may exit before request completes
     * Use trackSync for important events
     *
     * @param action - The action being performed
     * @param detail - Optional additional details
     */
    async track(action: TrackingAction, detail?: TrackingDetail): Promise<void> {
        try {
            const config = await this.configService.load();
            if (!config || !config.accessKey || !config.apiUrl) {
                return;
            }

            const trackUrl = `${config.apiUrl.replace(/\/$/, '')}/api/track`;

            // Fire and forget - don't await
            this.sendTrackingEvent(trackUrl, config.accessKey, action, detail).catch(
                () => {
                    // Silently ignore tracking errors
                }
            );
        } catch {
            // Silently ignore config errors
        }
    }

    /**
     * Track a CLI action synchronously (waits for completion)
     * Use this for important events before process exit
     */
    async trackSync(action: TrackingAction, detail?: TrackingDetail): Promise<boolean> {
        try {
            const config = await this.configService.load();
            if (!config || !config.accessKey || !config.apiUrl) {
                return false;
            }

            const trackUrl = `${config.apiUrl.replace(/\/$/, '')}/api/track`;
            return await this.sendTrackingEvent(trackUrl, config.accessKey, action, detail);
        } catch {
            return false;
        }
    }

    private async sendTrackingEvent(
        trackUrl: string,
        accessKey: string,
        action: TrackingAction,
        detail?: TrackingDetail
    ): Promise<boolean> {
        try {
            // Merge detail with system info
            const enrichedDetail = {
                ...getSystemInfo(),
                ...detail,
            };

            const response = await fetch(trackUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'JAI1-Access-Key': accessKey,
                },
                body: JSON.stringify({
                    action,
                    detail: enrichedDetail,
                    clientVersion: packageJson.version,
                }),
                // Timeout to avoid blocking too long
                signal: AbortSignal.timeout(10000),
            });

            return response.ok;
        } catch {
            return false;
        }
    }
}

// Singleton instance for convenience
let trackingInstance: TrackingService | null = null;

/**
 * Get or create tracking service instance
 */
export function getTrackingService(): TrackingService {
    if (!trackingInstance) {
        trackingInstance = new TrackingService();
    }
    return trackingInstance;
}

/**
 * Convenience function to track an action (fire-and-forget)
 * WARNING: May not complete before process exit
 */
export function trackAction(action: TrackingAction, detail?: TrackingDetail): void {
    getTrackingService().track(action, detail);
}

/**
 * Convenience function to track an action synchronously
 * Use this for important events at end of commands
 */
export async function trackActionSync(action: TrackingAction, detail?: TrackingDetail): Promise<boolean> {
    return getTrackingService().trackSync(action, detail);
}
